import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { ArrowLeft, Search, RefreshCw, Eye, Check, X, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const AdminDeliveryApplicationsPage = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [fullScreenImage, setFullScreenImage] = useState(null);

  // Fetch applications
  const fetchApplications = async () => {
    setLoading(true);
    try {
      const params = searchTerm ? { search: searchTerm } : {};
      const response = await axios.get(`${API}/admin/delivery-applications`, { params });
      setApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  // Search handler
  const handleSearch = (e) => {
    e.preventDefault();
    fetchApplications();
  };

  // View application details
  const viewDetails = async (applicationId) => {
    setDetailsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/delivery-applications/${applicationId}`);
      setSelectedApp(response.data);
    } catch (error) {
      console.error('Error fetching details:', error);
      toast.error('Failed to fetch application details');
    } finally {
      setDetailsLoading(false);
    }
  };

  // Approve application
  const approveApplication = async (applicationId) => {
    setActionLoading(applicationId);
    try {
      await axios.post(`${API}/admin/delivery-applications/${applicationId}/approve`);
      toast.success('Application approved!');
      fetchApplications();
      if (selectedApp?.id === applicationId) {
        setSelectedApp(prev => ({ ...prev, status: 'approved' }));
      }
    } catch (error) {
      console.error('Error approving:', error);
      toast.error('Failed to approve application');
    } finally {
      setActionLoading(null);
    }
  };

  // Reject application
  const rejectApplication = async (applicationId) => {
    setActionLoading(applicationId);
    try {
      await axios.post(`${API}/admin/delivery-applications/${applicationId}/reject`, {
        rejection_reason: ''
      });
      toast.success('Application rejected');
      fetchApplications();
      if (selectedApp?.id === applicationId) {
        setSelectedApp(prev => ({ ...prev, status: 'rejected' }));
      }
    } catch (error) {
      console.error('Error rejecting:', error);
      toast.error('Failed to reject application');
    } finally {
      setActionLoading(null);
    }
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${styles[status] || styles.pending}`}>
        {status || 'Pending'}
      </span>
    );
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Image preview component
  const ImagePreview = ({ src, label }) => {
    if (!src) return <div className="text-gray-400 text-sm">Not uploaded</div>;
    return (
      <div className="space-y-1">
        <p className="text-sm font-medium text-gray-700">{label}</p>
        <img 
          src={src} 
          alt={label}
          className="w-full h-40 object-cover rounded-lg border cursor-pointer hover:opacity-90"
          onClick={() => setFullScreenImage(src)}
        />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-6">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="p-2 hover:bg-gray-100 rounded-full">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold">Delivery Partner Applications</h1>
          </div>
        </div>
      </div>

      {/* Search and Refresh */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by name, mobile, WhatsApp, or Aadhaar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <Button type="submit" variant="outline" className="px-4" data-testid="search-button">
            Search
          </Button>
          <Button 
            type="button" 
            variant="outline" 
            onClick={fetchApplications}
            className="px-3"
            data-testid="refresh-button"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </form>
      </div>

      {/* Applications Table */}
      <div className="max-w-7xl mx-auto px-4">
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">User Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Mobile</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">WhatsApp</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Aadhaar</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Submitted</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" />
                      <p className="mt-2 text-gray-500">Loading applications...</p>
                    </td>
                  </tr>
                ) : applications.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                      No applications found
                    </td>
                  </tr>
                ) : (
                  applications.map((app) => (
                    <tr key={app.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                        {app.id?.slice(0, 8)}...
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {app.user_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{app.mobile_number}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{app.whatsapp_number}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                        {app.aadhar_number?.slice(0, 4)}****{app.aadhar_number?.slice(-4)}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={app.status} />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {formatDate(app.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => viewDetails(app.id)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                            title="View Details"
                            data-testid={`view-details-${app.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {app.status === 'pending' && (
                            <>
                              <button
                                onClick={() => approveApplication(app.id)}
                                disabled={actionLoading === app.id}
                                className="p-1.5 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                                title="Approve"
                                data-testid={`approve-${app.id}`}
                              >
                                {actionLoading === app.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Check className="w-4 h-4" />
                                )}
                              </button>
                              <button
                                onClick={() => rejectApplication(app.id)}
                                disabled={actionLoading === app.id}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                                title="Reject"
                                data-testid={`reject-${app.id}`}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Results count */}
        {!loading && applications.length > 0 && (
          <p className="mt-3 text-sm text-gray-500">
            Showing {applications.length} application{applications.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Details Modal */}
      {selectedApp && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b flex items-center justify-between bg-gray-50">
              <h2 className="text-lg font-bold">Application Details</h2>
              <button
                onClick={() => setSelectedApp(null)}
                className="p-2 hover:bg-gray-200 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {detailsLoading ? (
                <div className="py-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Application ID</p>
                      <p className="font-mono text-sm">{selectedApp.id}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Status</p>
                      <StatusBadge status={selectedApp.status} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">User Name</p>
                      <p className="font-medium">{selectedApp.user_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Submitted At</p>
                      <p className="text-sm">{formatDate(selectedApp.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Mobile Number</p>
                      <p className="text-sm">{selectedApp.mobile_number}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">WhatsApp Number</p>
                      <p className="text-sm">{selectedApp.whatsapp_number}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Aadhaar Number</p>
                      <p className="font-mono text-sm">{selectedApp.aadhar_number}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">PAN Number</p>
                      <p className="text-sm">{selectedApp.pan_number || '—'}</p>
                    </div>
                  </div>

                  {/* Documents */}
                  <div className="border-t pt-4">
                    <h3 className="font-semibold mb-4">Uploaded Documents</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <ImagePreview src={selectedApp.aadhar_photo_url} label="Aadhaar Card" />
                      <ImagePreview src={selectedApp.pancard_photo_url} label="PAN Card" />
                      <ImagePreview src={selectedApp.driving_license_photo_url} label="Driving License" />
                      <ImagePreview src={selectedApp.live_selfie_url} label="Live Selfie" />
                    </div>
                    <div className="mt-4">
                      <ImagePreview src={selectedApp.bike_bag_photo_url} label="Photo with Bike & Delivery Bag" />
                    </div>
                  </div>

                  {/* Rejection Reason */}
                  {selectedApp.rejection_reason && (
                    <div className="border-t pt-4">
                      <p className="text-xs text-gray-500 uppercase">Rejection Reason</p>
                      <p className="text-sm text-red-600">{selectedApp.rejection_reason}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            {selectedApp.status === 'pending' && !detailsLoading && (
              <div className="px-6 py-4 border-t bg-gray-50 flex gap-3">
                <Button
                  onClick={() => approveApplication(selectedApp.id)}
                  disabled={actionLoading === selectedApp.id}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  data-testid="modal-approve-button"
                >
                  {actionLoading === selectedApp.id ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  Approve
                </Button>
                <Button
                  onClick={() => rejectApplication(selectedApp.id)}
                  disabled={actionLoading === selectedApp.id}
                  variant="outline"
                  className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                  data-testid="modal-reject-button"
                >
                  <X className="w-4 h-4 mr-2" />
                  Reject
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Full Screen Image Modal */}
      {fullScreenImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4"
          onClick={() => setFullScreenImage(null)}
        >
          <button
            className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-full"
            onClick={() => setFullScreenImage(null)}
          >
            <X className="w-6 h-6" />
          </button>
          <img 
            src={fullScreenImage} 
            alt="Full view"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default AdminDeliveryApplicationsPage;
